//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

import tidalSelect from './tidal-select.vue';

/**
 * @typedef {{ name: string, value: number }} DateValue
 */

const DEFAULT_VALUES = {
    day: 0,
    month: 0,
    year: 0,
};

const DEFAULT_TRANSLATIONS = {
    emptyError: 'You must fill out all the fields',
    ageError: 'You must be at least %s years old',
    invalidDateError: 'The date you entered is invalid',
    months: {
        jan: 'January',
        feb: 'February',
        mar: 'March',
        apr: 'April',
        may: 'May',
        jun: 'June',
        jul: 'July',
        aug: 'August',
        sep: 'September',
        oct: 'October',
        nov: 'November',
        dec: 'December',
    },
    dayPlaceholder: 'Day',
    monthPlaceholder: 'Month',
    yearPlaceholder: 'Year',
};

export default {
    components: {
        tidalSelect,
    },
    model: {
        prop: 'timestamp',
        event: 'change',
    },
    props: {
        /**
         * Sets the component to use and update the model passed through v-model prop.
         *
         * When useModel is true, the v-model property must also be set by the user of this
         * component. The model takes a JS timestamp (interpreted in UTC format) or null.
         *
         * Model is updated on changing the view and is set to null when date is invalid or there
         * is other kind of error.
         *
         * When using model, the `defaultValues` property is ignored and `selected` event is not
         * dispatched on date change.
         */
        useModel: {
            type: Boolean,
            default: false,
        },
        // The prop used by the model. Not to be set directly by the user or changed internally.
        // Updated indirectly through dispatch of `change` event.
        timestamp: {
            type: Number,
            default: null,
        },
        name: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            default: null,
        },
        minAge: {
            type: Number,
            default: 13,
        },
        maxAge: {
            type: Number,
            default: 100,
        },
        required: {
            type: Boolean,
            default: false,
        },
        error: {
            type: Boolean,
            default: false,
        },
        success: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        isClient: {
            type: Boolean,
            default: false,
        },
        /** @type {import('vue').PropOptions<typeof DEFAULT_VALUES>} */
        defaultValues: {
            type: Object,
            default() {
                return DEFAULT_VALUES;
            },
        },
        /** @type {import('vue').PropOptions<typeof DEFAULT_TRANSLATIONS>} */
        translations: {
            type: Object,
            default() {
                return DEFAULT_TRANSLATIONS;
            },
        },
        countrycode: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            /** @type {number | string} */
            day: 0,
            /** @type {number | string} */
            month: 0,
            /** @type {number | string} */
            year: 0,
            isDone: false,
            maxNumberOfDays: 31,
            initialRun: true,
            invalidDate: false,
        };
    },
    computed: {
        /** @return {boolean} */
        emptyError() {
            return this.initialRun && this.year && !(this.month && this.day) ? true : false;
        },
        /** @return {boolean} */
        ageError() {
            return !this.emptyError && this.birthdayString > this.minimumAge ? true : false;
        },
        /** @return {boolean} */
        hasError() {
            return this.error || this.emptyError || this.ageError || this.invalidDate;
        },
        /** @return {boolean} */
        usFormat() {
            return Boolean(this.countrycode && this.countrycode.toLowerCase() === 'us');
        },
        days() {
            /** @type {import('./tidal-select.vue').SelectValue[]} */
            const days = [];
            for (let i = 1; i <= this.maxNumberOfDays; i++) {
                days.push({ name: String(i), value: i });
            }
            return days;
        },
        months() {
            let i = 1;
            /** @type {import('./tidal-select.vue').SelectValue[]} */
            const months = [];
            const monthKeys = /** @type {Array<keyof DEFAULT_TRANSLATIONS['months']>} */(Object.keys(this.translations.months));
            for (const month of monthKeys) {
                months.push({ name: this.translations.months[month], value: i++ });
            }
            return months;
        },
        years() {
            const date = new Date();
            /** @type {import('./tidal-select.vue').SelectValue[]} */
            const years = [];
            for (let i = 0; i < this.maxAge + 1; i++) {
                const year = date.getFullYear() - i;
                years.push({ name: String(year), value: year });
            }
            return years;
        },
        /** @return {number} */
        minimumAge() {
            const date = new Date();

            return this.buildBirthdayString(
                date.getDate(),
                date.getMonth() + 1,
                date.getFullYear() - this.minAge,
            );
        },
        /** @return {number} */
        birthdayString() {
            return this.buildBirthdayString(
                this.day,
                this.month,
                this.year,
            );
        },
        /** @return {string | undefined} */
        preselectedDay() {
            if (this.day > 0) {
                return this.day.toString();
            } else if (!this.useModel && this.defaultValues && this.defaultValues.day && this.initialRun) {
                return this.defaultValues.day.toString();
            }

            return undefined;
        },
        /** @return {string | undefined} */
        preselectedMonth() {
            if (this.useModel) {
                if (this.month > 0) {
                    return String(this.month);
                }
            } else if (this.defaultValues && this.defaultValues.month) {
                return this.defaultValues.month.toString();
            }

            return undefined;
        },
        /** @return {string | undefined} */
        preselectedYear() {
            if (this.useModel) {
                if (this.year > 0) {
                    return String(this.year);
                }
            } else if (this.defaultValues && this.defaultValues.year) {
                return this.defaultValues.year.toString();
            }

            return undefined;
        },
    },
    watch: {
        /** @type {import('vue').WatchHandler<number>} */
        birthdayString(birthday) {
            /*
             * if the day picked is invalid or the number of days does not correspond
             * to the month picked for the month/year picked, reset and recalculate
             * the maximum amount of days
             */
            // run the validations only when we have day, month and year
            if (birthday.toString().length === 8) {
                const fullYearPicked = Number(birthday.toString().substring(0, 4));
                // zero based
                const monthPicked = Number(birthday.toString().substring(4, 6)) - 1;
                const dayPicked = Number(birthday.toString().substring(6, 8));
                const daysInMonth = new Date(fullYearPicked, monthPicked + 1, 0).getDate();
                this.maxNumberOfDays = daysInMonth;
                if (dayPicked <= daysInMonth && dayPicked !== 0 && monthPicked !== -1) {
                    // since the day is valid, for the month picked set it again
                    this.day = dayPicked.toString();
                    this.invalidDate = false;
                    this.initialRun = false;
                } else if (dayPicked > daysInMonth) {
                    this.day = '0';
                    this.invalidDate = true;
                }
            }
            this.isDone = !this.emptyError && !this.ageError && !this.invalidDate && this.day
                && this.month && this.year && birthday <= this.minimumAge ? true : false;

            if (this.useModel) {
                let newTimestamp = null;

                if (this.isDone) {
                    newTimestamp = new Date(Date.UTC(
                        Number.parseInt(String(this.year), 10),
                        Number.parseInt(String(this.month), 10) - 1,
                        Number.parseInt(String(this.day), 10),
                    )).getTime();
                }

                /**
                 * @event change
                 * @property {number | null} newTimestamp
                 */
                this.$emit('change', newTimestamp);
            } else {
                /**
                 * @event selected
                 * @property {string} name
                 * @property {number | boolean} value
                 */
                this.$emit('selected', {
                    name: this.name,
                    value: this.isDone ? birthday : false,
                });
            }
        },
    },
    created() {
        if (this.useModel) {
            if (this.timestamp) {
                const date = new Date(this.timestamp);
                this.day = date.getUTCDate() || 0;
                this.month = (date.getUTCMonth() + 1) || 0;
                this.year = date.getUTCFullYear() || 0;
            }
        } else if (this.defaultValues) {
            this.day = this.defaultValues.day;
            this.month = this.defaultValues.month;
            this.year = this.defaultValues.year;
        }
    },
    methods: {
        /**
         * @param {(number|string)} d
         * @return {string}
         */
        padNumber(d) {
            if (typeof d !== 'number') {
                d = Number(d);
            }

            return d < 10 ? '0' + d.toString() : d.toString();
        },
        /**
         * Builds a birthday string.
         *
         * @param {number | string} day The day
         * @param {number | string} month The month
         * @param {number | string} year The year
         * @return {number} The birthday string.
         */
        buildBirthdayString(day, month, year) {
            return Number(this.padNumber(year) + this.padNumber(month) + this.padNumber(day));
        },
    },
};
